import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ShieldCheck, Plus, KeyRound, Coins, Building2, CreditCard, CheckCircle2 } from "lucide-react";

export default function CompanyPortal() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(user?.role === "company");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    company: user?.display_name || "",
    title: "",
    description: "",
    budget: "",
    deadline: "",
    bounty_amount: "",
    bounty_currency: "USD",
    postKind: "enquiry",
  });

  useEffect(() => {
    if (unlocked) api.get("/company/mine").then((r) => setMine(r.data)).catch(() => {});
  }, [unlocked]);

  useEffect(() => {
    setUnlocked(user?.role === "company");
    if (user) setForm((f) => ({ ...f, company: f.company || user.display_name }));
  }, [user]);

  if (!user) return <Navigate to="/register" />;

  const verifyCode = async () => {
    if (!code.trim()) return toast.error("Enter your access code");
    setBusy(true);
    try {
      const { data } = await api.post("/company/verify-code", { code: code.trim() });
      if (data.upgraded && data.user) {
        setUser(data.user);
        localStorage.setItem("eco_user", JSON.stringify(data.user));
        toast.success("Access granted — you are now a company account");
      } else {
        toast.success("Welcome back");
      }
      setUnlocked(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const post = async () => {
    if (!form.company || !form.title || form.description.length < 10) return toast.error("Fill company, title and description");
    setBusy(true);
    try {
      const payload = {
        company: form.company,
        title: `[${form.postKind.toUpperCase()}] ${form.title}`,
        description: form.description,
        budget: form.budget,
        deadline: form.deadline,
        bounty_amount: form.bounty_amount ? parseFloat(form.bounty_amount) : null,
        bounty_currency: form.bounty_currency,
      };
      await api.post("/company/requests", payload);
      toast.success("Posted publicly under Companies feed");
      setForm({ ...form, title: "", description: "", budget: "", deadline: "", bounty_amount: "" });
      const r = await api.get("/company/mine"); setMine(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const fundBounty = async (id) => {
    try {
      const { data } = await api.post("/bounties/fund", { request_id: id, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!unlocked) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24">
        <div className="mono text-xs text-primary">// companies · portal</div>
        <h1 className="mt-2 text-4xl font-black tracking-tighter">Restricted access.</h1>
        <p className="mt-3 text-sm text-muted-foreground">Enter your reference code to unlock the company posting interface. Codes are issued to verified organisations — reach out at hello@eco-chamber.local if you don't have one.</p>
        <div className="mt-8 border border-primary/40 p-6">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Lock className="h-4 w-4" />Reference code</div>
          <Label className="mt-4 block text-xs uppercase tracking-widest text-muted-foreground">Access code</Label>
          <Input data-testid="portal-code" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verifyCode()} className="mt-2 rounded-none bg-input mono uppercase" placeholder="ECO-COMPANY-XXXX" />
          <Button data-testid="portal-submit" onClick={verifyCode} disabled={busy} className="mt-4 w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
            <KeyRound className="mr-2 h-4 w-4" />{busy ? "Verifying…" : "Unlock portal"}
          </Button>
        </div>
        <div className="mt-6 text-xs text-muted-foreground">
          Not a company? <button onClick={() => nav("/companies")} className="text-primary hover:underline">← back to public feed</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary flex items-center gap-2"><ShieldCheck className="h-3 w-3" />companies · portal · verified</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Post to the community.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Post enquiries, idea requests or open issues. Attach a bounty to draw the best minds.</p>
        </div>
        <Button variant="outline" onClick={() => nav("/companies")} className="rounded-none" data-testid="portal-view-feed"><Building2 className="mr-2 h-4 w-4" />Public feed</Button>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <div className="border border-primary/40 p-6 space-y-5 h-fit">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Plus className="h-3 w-3" />New post</div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Post kind</Label>
            <div className="mt-2 grid grid-cols-3 gap-1">
              {["enquiry", "idea-request", "issue"].map((k) => (
                <button key={k} data-testid={`kind-${k}`} onClick={() => setForm({ ...form, postKind: k })} className={`border p-2 text-xs uppercase tracking-widest ${form.postKind === k ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>{k.replace("-", " ")}</button>
              ))}
            </div>
          </div>

          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Company</Label><Input data-testid="portal-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label><Input data-testid="portal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label><Textarea data-testid="portal-desc" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Budget</Label><Input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="$5-15k" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Deadline</Label><Input value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="Q2 2026" /></div>
          </div>
          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Coins className="h-3 w-3" />Bounty (optional)</div>
            <p className="mt-1 text-xs text-muted-foreground">Awarded to the responder when you accept their answer.</p>
            <div className="mt-3 grid grid-cols-[1fr_100px] gap-2">
              <Input data-testid="portal-bounty" type="number" step="1" min="0" value={form.bounty_amount} onChange={(e) => setForm({ ...form, bounty_amount: e.target.value })} className="rounded-none bg-input" placeholder="e.g. 2500" />
              <select value={form.bounty_currency} onChange={(e) => setForm({ ...form, bounty_currency: e.target.value })} className="rounded-none bg-input border border-border p-2 text-sm">
                <option>USD</option><option>EUR</option><option>GBP</option><option>INR</option>
              </select>
            </div>
          </div>
          <Button onClick={post} disabled={busy} data-testid="portal-post" className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
            {busy ? "Posting…" : "Publish to public feed"}
          </Button>
        </div>

        <div>
          <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Your posts ({mine.length})</div>
          {mine.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No posts yet — publish your first challenge on the left.</div>
          ) : (
            <div className="border border-border">
              {mine.map((r, i) => (
                <div key={r.id} data-testid={`mine-${r.id}`} className={`p-4 ${i > 0 ? "border-t border-border" : ""}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-0.5 border uppercase tracking-widest ${r.status === "solved" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{r.status || "open"}</span>
                    {r.bounty_amount ? <span className="mono text-primary flex items-center gap-1"><Coins className="h-3 w-3" />{r.bounty_currency} {r.bounty_amount}{r.bounty_funded ? " · funded" : ""}</span> : <span className="text-muted-foreground">{r.response_count} replies</span>}
                  </div>
                  <h3 className="mt-2 font-bold tracking-tight line-clamp-1">{r.title}</h3>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{r.description}</p>
                  {r.bounty_amount && !r.bounty_funded && (
                    <button data-testid={`fund-${r.id}`} onClick={() => fundBounty(r.id)} className="mt-3 inline-flex items-center gap-1 border border-primary bg-primary/10 hover:bg-primary/20 text-primary px-3 py-1 text-[10px] uppercase tracking-widest">
                      <CreditCard className="h-3 w-3" />Fund bounty via Stripe
                    </button>
                  )}
                  {r.bounty_funded && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary">
                      <CheckCircle2 className="h-3 w-3" />Escrow held
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
